import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Icon from '../components/brand/Icon';
import './DashboardPage.css';

// TODO: Replace sample activity with live data from the backend.
const RECENT_ACTIVITY = [
  { id: 1, customer: 'Plate ABC1234', amount: 5.00, status: 'paid', method: 'Permit', date: 'Jun 20, 2026' },
  { id: 2, customer: 'Plate TXR8890', amount: 0, status: 'validated', method: 'Validation', date: 'Jun 20, 2026' },
  { id: 3, customer: 'Plate GHK2201', amount: 5.00, status: 'paid', method: 'Permit', date: 'Jun 20, 2026' },
  { id: 4, customer: 'Plate LMN5567', amount: 0, status: 'validated', method: 'Validation', date: 'Jun 19, 2026' },
  { id: 5, customer: 'Plate PQR3344', amount: 5.00, status: 'paid', method: 'Permit', date: 'Jun 19, 2026' },
];

const statusBadge = (status) => {
  const map = { paid: 'badge-success', validated: 'badge-gray', pending: 'badge-warning' };
  const label = { paid: 'Paid', validated: 'Validated', pending: 'Pending' };
  return <span className={`badge ${map[status] || 'badge-gray'}`}>{label[status] || status}</span>;
};

const DashboardPage = () => {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    setAuthed(!!token);
  }, []);

  if (authed === null) return null;

  if (!authed) {
    return (
      <div className="dashboard-page">
        <Navbar />
        <div className="dashboard-gate">
          <div className="gate-card">
            <div className="gate-icon"><Icon name="lock" size={30} /></div>
            <h2>Sign in to access your dashboard</h2>
            <p>Manage your parking payments, validations, and enforcement in one place.</p>
            <div className="gate-actions">
              <Link to="/admin/signin" className="btn btn-primary btn-lg">Login</Link>
              <Link to="/business-signup" className="btn btn-outline btn-lg">Create Account</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const permitSales = RECENT_ACTIVITY.filter(t => t.status === 'paid');
  const totalRevenue = permitSales.reduce((s, t) => s + t.amount, 0);
  const validations = RECENT_ACTIVITY.filter(t => t.status === 'validated').length;

  return (
    <div className="dashboard-page">
      <Navbar />
      <div className="dashboard-content container">
        <div className="dashboard-header">
          <div>
            <h1>Business Dashboard</h1>
            <p className="dashboard-subtitle">Your parking payments and enforcement at a glance.</p>
          </div>
          <div className="dashboard-header-actions">
            <Link to="/enforcement" className="btn btn-outline btn-sm">Enforcement Lookup</Link>
            <Link to="/qr-pay" className="btn btn-primary btn-sm">Create QR Sign</Link>
          </div>
        </div>

        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon green"><Icon name="dollar" size={22} /></div>
            <div className="stat-body">
              <div className="stat-label">Revenue Overview</div>
              <div className="stat-value">${totalRevenue.toFixed(2)}</div>
              <div className="stat-sub">This period</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon blue"><Icon name="ticket" size={22} /></div>
            <div className="stat-body">
              <div className="stat-label">Permit Sales</div>
              <div className="stat-value">{permitSales.length}</div>
              <div className="stat-sub">Permits sold</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon teal"><Icon name="check" size={22} /></div>
            <div className="stat-body">
              <div className="stat-label">Validations</div>
              <div className="stat-value">{validations}</div>
              <div className="stat-sub">Customers validated</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon purple"><Icon name="pin" size={22} /></div>
            <div className="stat-body">
              <div className="stat-label">Locations</div>
              <div className="stat-value">1</div>
              <div className="stat-sub">Active lots</div>
            </div>
          </div>
        </div>

        {/* Account setup status */}
        <div className="card dashboard-setup">
          <div className="setup-head">
            <h2>Account Setup</h2>
            <span className="badge badge-warning">1 step left</span>
          </div>
          <div className="setup-steps">
            <div className="setup-step done">
              <span className="setup-check"><Icon name="check" size={16} /></span>
              <span>Business account created</span>
            </div>
            <div className="setup-step done">
              <span className="setup-check"><Icon name="check" size={16} /></span>
              <span>Parking lot added</span>
            </div>
            <div className="setup-step">
              <span className="setup-dot" />
              <span>Connect payouts to start receiving funds</span>
              <Link to="/admin/dashboard" className="setup-action">Finish setup</Link>
            </div>
          </div>
        </div>

        <div className="dashboard-grid">
          {/* Recent Activity */}
          <div className="card dashboard-transactions">
            <div className="card-header">
              <h2>Recent Activity</h2>
              <Link to="/invoices" className="btn btn-sm btn-outline">View All</Link>
            </div>
            <div className="transactions-table">
              <table>
                <thead>
                  <tr>
                    <th>Plate</th>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {RECENT_ACTIVITY.map((t) => (
                    <tr key={t.id}>
                      <td className="td-customer">{t.customer}</td>
                      <td className="td-method">{t.method}</td>
                      <td className="td-date">{t.date}</td>
                      <td className="td-amount">{t.amount ? `$${t.amount.toFixed(2)}` : 'Free'}</td>
                      <td>{statusBadge(t.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card dashboard-actions">
            <h2>Quick Actions</h2>
            <div className="quick-actions">
              <Link to="/qr-pay" className="quick-action">
                <span className="qa-icon"><Icon name="qr" size={20} /></span>
                <div>
                  <div className="qa-title">QR Signs</div>
                  <div className="qa-desc">Generate printable lot signage</div>
                </div>
              </Link>
              <Link to="/enforcement" className="quick-action">
                <span className="qa-icon"><Icon name="search" size={20} /></span>
                <div>
                  <div className="qa-title">Enforcement Lookup</div>
                  <div className="qa-desc">Check a license plate</div>
                </div>
              </Link>
              <Link to="/payment-link" className="quick-action">
                <span className="qa-icon"><Icon name="link" size={20} /></span>
                <div>
                  <div className="qa-title">Payment Link</div>
                  <div className="qa-desc">Share a link to collect a payment</div>
                </div>
              </Link>
              <Link to="/admin/dashboard" className="quick-action">
                <span className="qa-icon"><Icon name="settings" size={20} /></span>
                <div>
                  <div className="qa-title">Lot Management</div>
                  <div className="qa-desc">Manage parking zones and payouts</div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
